import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ForkModuleError,
  useForkPublicModuleMutation,
  usePublicModulesQuery,
  type PublicModule,
  type PublicModulePage,
} from '@entities/PublicModule';
import { Pagination, toast } from '@shared/ui';
import { PublicModuleCard } from './PublicModuleCard';
import './Library.css';

const PAGE_SIZE = 12;

/** «Добавить» прямо из каталога: копия модуля без перехода, кнопка становится «Добавлено». */
function useAddFromCatalog() {
  const { t } = useTranslation();
  const fork = useForkPublicModuleMutation();
  const [addingId, setAddingId] = useState<string | null>(null);
  // Добавленные в этой сессии страницы — пока каталог не перечитан.
  const [addedIds, setAddedIds] = useState<ReadonlySet<string>>(() => new Set());
  const markAdded = (id: string) => setAddedIds((prev) => new Set(prev).add(id));

  const add = (m: PublicModule) => {
    if (addingId) return;
    setAddingId(m.id);
    fork.mutate(m.id, {
      onSuccess: () => {
        markAdded(m.id);
        toast({ tone: 'success', message: t('library.addedToast', { name: m.name }) });
      },
      onError: (err) => {
        if (err instanceof ForkModuleError && err.code === 'ALREADY_ADDED') markAdded(m.id);
        else toast({ tone: 'error', message: t('library.forkError') });
      },
      onSettled: () => setAddingId(null),
    });
  };

  return { add, addingId, isAdded: (m: PublicModule) => m.isAdded || addedIds.has(m.id) };
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

interface SectionProps {
  title: string;
  page: PublicModulePage | undefined;
  onPage: (page: number) => void;
  children: (m: PublicModule) => ReactNode;
}

function CatalogSection({ title, page, onPage, children }: SectionProps) {
  const { t } = useTranslation();
  if (!page || page.total === 0) return null;
  return (
    <section className="lb-section">
      <div className="lb-section-head">
        <h2 className="lb-section-title">{title}</h2>
        <span className="lb-section-count">{t('library.moduleCount', { count: page.total })}</span>
      </div>
      <div className="lb-grid">{page.items.map(children)}</div>
      <Pagination page={page.page} pageSize={page.pageSize} total={page.total} onChange={onPage} />
    </section>
  );
}

/**
 * Каталог публичных модулей: поиск и два раздела —
 * «От Lomora» (владелец — системный аккаунт) и «От сообщества». У каждого
 * раздела своя пагинация; счётчик — всего модулей раздела под поиском.
 */
export function LibraryGrid() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [officialPage, setOfficialPage] = useState(1);
  const [communityPage, setCommunityPage] = useState(1);
  const filters = { pageSize: PAGE_SIZE, search };
  const official = usePublicModulesQuery({ ...filters, page: officialPage, official: true });
  const community = usePublicModulesQuery({ ...filters, page: communityPage, official: false });
  const catalog = useAddFromCatalog();

  const resetPages = () => {
    setOfficialPage(1);
    setCommunityPage(1);
  };
  const filtered = search.trim() !== '';
  const loading = official.isPending || community.isPending;
  const empty = !loading && !official.data?.total && !community.data?.total;

  const card = (m: PublicModule) => (
    <PublicModuleCard
      key={m.id}
      module={m}
      added={catalog.isAdded(m)}
      adding={catalog.addingId === m.id}
      onAdd={catalog.add}
    />
  );

  return (
    <div>
      <div className="lb-toolbar">
        <label className="lb-search">
          <SearchIcon />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPages();
            }}
            placeholder={t('library.searchPlaceholder')}
            aria-label={t('library.searchPlaceholder')}
          />
        </label>
      </div>

      {loading ? (
        <div className="lb-grid" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="lb-skel animate-skeleton" />
          ))}
        </div>
      ) : empty ? (
        <div className="lb-empty">{t(filtered ? 'library.empty' : 'library.emptyAll')}</div>
      ) : (
        <>
          <CatalogSection title={t('library.official')} page={official.data} onPage={setOfficialPage}>
            {card}
          </CatalogSection>
          <CatalogSection title={t('library.community')} page={community.data} onPage={setCommunityPage}>
            {card}
          </CatalogSection>
        </>
      )}
    </div>
  );
}
